import { useState } from 'react'
import { Button, Modal, Anchor, Badge } from '@mantine/core'
import { SwitchHorizontal01, ReverseLeft } from '@untitled-ui/icons-react'
import dayjs from '@/lib/dayjs'
import CreateFulfillmentOrderModal from '@/components/service_cases/CreateFulfillmentOrderModal'

const foStatuses = {
    OPEN: { label: 'À préparer', color: 'blue' },
    IN_PROGRESS: { label: 'En cours', color: 'orange' },
    CLOSED: { label: 'Terminé', color: 'green' },
    CANCELLED: { label: 'Annulé', color: 'red' },
}

const parallelActionConfigs = {
    swap_same: {
        flag: 'swapped_at',
        title: 'Échange à créer',
        doneTitle: 'Échange effectué',
        description: 'Le diagnostic a été validé. Un bon de préparation doit être créé pour envoyer le remplacement au client.',
        buttonLabel: 'Marquer l\'échange comme créé',
        confirmDescription: 'L\'échange a bien été créé.',
        icon: SwitchHorizontal01,
        canCreateFO: true,
    },
    return: {
        flag: 'refunded_at',
        title: 'Remboursement à effectuer',
        doneTitle: 'Remboursement effectué',
        description: 'Le diagnostic a été validé. Le remboursement doit être effectué auprès du client.',
        buttonLabel: 'Marquer le remboursement comme effectué',
        confirmDescription: 'Le remboursement a bien été effectué.',
        icon: ReverseLeft,
        canCreateFO: false,
    },
}

export default function ServiceCaseParallelActions({ serviceCase, performAction, reload }) {
    const [loading, setLoading] = useState(false)
    const [confirmOpened, setConfirmOpened] = useState(false)
    const [foModalOpened, setFoModalOpened] = useState(false)

    const config = parallelActionConfigs[serviceCase.type]

    // Ne rien afficher si pas de config (type repair) ou diagnostic pas encore validé
    if (!config || !serviceCase.diagnosis_cleared_at) return null

    const isDone = !!serviceCase[config.flag]
    const doneDate = isDone
        ? dayjs(serviceCase[config.flag]).format('D MMM YYYY à HH:mm')
        : null
    const Icon = config.icon

    const handleConfirm = async () => {
        setLoading(true)
        try {
            await performAction('toggle_flag', { flag: config.flag })
        } catch (e) {
            // Error handled by useServiceCase
        } finally {
            setLoading(false)
            setConfirmOpened(false)
        }
    }

    const fulfillmentOrders = (serviceCase.fulfillment_orders || []).filter(fo => fo.status !== 'CANCELLED')

    const foList = fulfillmentOrders.length > 0 && (
        <div className="flex flex-col gap-2">
            {fulfillmentOrders.map((fo) => (
                <div
                    key={fo.id}
                    className="flex items-center justify-between border rounded-lg px-4 py-3"
                >
                    <div className="flex flex-col">
                        <Anchor href={`/fulfillment_orders/${fo.id}`} size="sm">
                            BP{fo.id}
                        </Anchor>
                        <span className="text-xs text-gray-400">
                            Créé le {dayjs(fo.created_at).format('D MMM YYYY')}
                        </span>
                    </div>
                    <Badge
                        color={foStatuses[fo.status]?.color || 'gray'}
                        variant="light"
                    >
                        {foStatuses[fo.status]?.label || fo.status}
                    </Badge>
                </div>
            ))}
        </div>
    )

    if (isDone) {
        return (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                    <Icon width={20} height={20} className="text-green-600 shrink-0" />
                    <div className="flex flex-col">
                        <span className="font-medium text-green-700">{config.doneTitle}</span>
                        <span className="text-sm text-green-600">{doneDate}</span>
                    </div>
                </div>
                {foList}
            </div>
        )
    }

    return (
        <>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex flex-col gap-4">
                <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-2">
                        <Icon width={20} height={20} className="text-blue-500" />
                        <span className="font-medium text-lg">{config.title}</span>
                    </div>
                    <span className="text-sm text-blue-500 text-center">
                        {config.description}
                    </span>
                </div>

                <div className="flex justify-center gap-3">
                    {config.canCreateFO && (
                        <Button
                            variant="default"
                            onClick={() => setFoModalOpened(true)}
                        >
                            Créer un bon de préparation
                        </Button>
                    )}
                    <Button onClick={() => setConfirmOpened(true)}>
                        {config.buttonLabel}
                    </Button>
                </div>

                {foList}
            </div>

            <Modal
                opened={confirmOpened}
                onClose={() => setConfirmOpened(false)}
                title={config.buttonLabel}
                centered
            >
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-500">
                        {config.confirmDescription}
                    </p>
                    <Button
                        fullWidth
                        loading={loading}
                        onClick={handleConfirm}
                    >
                        Confirmer
                    </Button>
                </div>
            </Modal>

            {config.canCreateFO && (
                <CreateFulfillmentOrderModal
                    serviceCase={serviceCase}
                    opened={foModalOpened}
                    onClose={() => setFoModalOpened(false)}
                    onSuccess={reload}
                />
            )}
        </>
    )
}
